import { useMemo } from "react";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { buildJobColorResolver, type JobColorPins, type JobColorResolver } from "../utils/categoricalColors";
import type { Job } from "../utils/models";
import { useAllJobs } from "./useJobs";
import { useJobColorPins } from "./useUserSettings";

const NO_PINS: JobColorPins = {};
const NO_JOBS: Job[] = [];
const NO_IDS: string[] = [];
/** Screens show what is saved; only the settings panel previews pins that aren't. */
const SAVED_PINS_ONLY = undefined;

/**
 * Colour per job id for the chosen department, with the user's (or the department default's) pinned colours
 * applied. The department's enabled jobs get the distinct colours; the rest of the catalog (jobs on old shifts,
 * pickers that list every job) follows after them.
 */
export const useJobColors = (): JobColorResolver => useJobColorResolver(SAVED_PINS_ONLY);

/**
 * The resolver behind `useJobColors`, optionally with pins that aren't saved yet (the settings panel previews
 * them), so the panel and every screen resolve colours from the same jobs the same way.
 */
export const useJobColorResolver = (pendingPins: JobColorPins | undefined): JobColorResolver => {
	const { chosenDepartment } = useHospitalData();
	const loadedPins = useJobColorPins();
	const { data: catalog } = useAllJobs();
	// DEFAULT: until settings load (once per department — the query never goes stale) jobs show their
	// automatic colours; a pinned job repaints when they arrive.
	const pins = pendingPins ?? loadedPins ?? NO_PINS;
	// DEFAULT: a department that doesn't use jobs carries no job list, and the catalog is still loading or
	// absent — whatever is missing simply isn't coloured yet.
	const departmentJobs = chosenDepartment.jobs ?? NO_JOBS;
	const enabledJobIds = chosenDepartment.jobIds ?? NO_IDS;
	const allJobs = catalog ?? NO_JOBS;

	return useMemo(() => {
		const known = new Set(departmentJobs.map((job) => job.id));
		const jobs = [...departmentJobs, ...allJobs.filter((job) => !known.has(job.id))];
		return buildJobColorResolver({ jobs, enabledJobIds, pins });
	}, [departmentJobs, allJobs, enabledJobIds, pins]);
};
